import { HoodType, type DeviceViewDTO, type GetApiDeviceParams } from '@smartsash/api-client';
import type { TableMeta } from '@tanstack/react-table';
import type { z } from 'zod';

import { ADMIN_LIST_SEARCH_DEFAULTS, createAdminListSearchSchema } from '@/router/listSearch';
import { firstNonEmpty } from '@/utils/firstNonEmpty';
import {
  buildFilterParams,
  collectSearchTerms,
  sortParams,
  type FilterParamMap,
} from '@/utils/listQueryParams';

export const devicesSearchSchema = createAdminListSearchSchema();

export type DevicesSearch = z.infer<typeof devicesSearchSchema>;

/**
 * The builder field ids that reach `GetApiDeviceParams` — Devices is the reference screen for the
 * filtering pattern because it is the one with the most server filters.
 *
 * `laboratory`, `firmwareVersion` and `osVersion` are the table's own columns — the first
 * re-vocabularied so it carries lab ids rather than the names it renders, the other two offering the
 * versions the loaded rows report. Institution, group, hood type and online status are not columns
 * at all and are added as extra fields.
 */
export const DEVICES_LAB_FIELD = 'laboratory';
export const DEVICES_INSTITUTION_FIELD = 'institutionId';
export const DEVICES_GROUP_FIELD = 'groupId';
export const DEVICES_ONLINE_FIELD = 'online';
export const DEVICES_HOOD_TYPE_FIELD = 'hoodType';
export const DEVICES_FIRMWARE_FIELD = 'firmwareVersion';
export const DEVICES_OS_FIELD = 'osVersion';

/**
 * The hood types the endpoint accepts, taken from the generated enum rather than written out.
 *
 * `HoodType` is closed on the API, so the vocabulary is the enum itself rather than whatever the
 * loaded page happens to contain — a filter that can only offer the values already on screen cannot
 * find the rows that are not.
 *
 * A function rather than a module-scope constant: `devicesModel` is imported widely (RowActions,
 * details pages) for its string constants alone, and several of those tests fully replace
 * `@smartsash/api-client` without `HoodType` — evaluating `Object.values(HoodType)` at import time
 * would crash every one of them. Deferring the read to call time means only `DevicesFilter`, which
 * actually calls this, needs the enum.
 */
export function deviceHoodTypeOptions() {
  return Object.values(HoodType).map((value) => ({ value, label: value }));
}

/**
 * Column id → the endpoint's `sortBy` value.
 *
 * `GET /api/Device` defaults to `serialNumber` rather than to a name, because a device has no name
 * field. Its whitelist covers every column this table shows.
 */
export const DEVICES_SORT_KEYS: Readonly<Record<string, string>> = {
  model: 'model',
  serialNumber: 'serialNumber',
  identifier: 'identifier',
  firmwareVersion: 'firmwareVersion',
  osVersion: 'osVersion',
};

/**
 * Every builder field `buildFilterParams` can turn into a real `GetApiDeviceParams` condition.
 * Devices is the screen with the most server-side filters: four of the seven have no column and
 * live only in the filter builder.
 */
export const DEVICES_FILTER_PARAMS: FilterParamMap<GetApiDeviceParams> = {
  [DEVICES_LAB_FIELD]: { kind: 'multi', param: 'labId' },
  [DEVICES_INSTITUTION_FIELD]: { kind: 'multi', param: 'institutionId' },
  [DEVICES_GROUP_FIELD]: { kind: 'multi', param: 'groupId' },
  [DEVICES_HOOD_TYPE_FIELD]: { kind: 'multi', param: 'hoodType' },
  [DEVICES_FIRMWARE_FIELD]: { kind: 'multi', param: 'firmwareVersion' },
  [DEVICES_OS_FIELD]: { kind: 'multi', param: 'osVersion' },
  [DEVICES_ONLINE_FIELD]: { kind: 'boolean', param: 'online' },
};

/**
 * Turn the URL's `page`/`q`/`sort`/`filters` into `GetApiDeviceParams` (HS-869 follow-up). Shared
 * by the screen's own `query` and `DevicesFilter`'s cache read, which have to build the exact same
 * params or the filter panel would read a different cache entry than the table renders from.
 */
export function devicesQueryParams(search: DevicesSearch): GetApiDeviceParams {
  const conditions = search.filters ?? [];
  const searchTerms = collectSearchTerms(conditions, search.q ?? '');
  return {
    page: search.page,
    pageSize: search.pageSize,
    ...sortParams<GetApiDeviceParams>(search.sort, DEVICES_SORT_KEYS),
    ...(searchTerms ? { search: searchTerms } : {}),
    ...buildFilterParams(conditions, DEVICES_FILTER_PARAMS),
  };
}

/**
 * The text each column holds for a row (HS-869). `laboratory` takes the table's `meta`:
 * `DeviceViewDTO` carries only `labId`, and the name is joined client-side, falling back to the id
 * exactly as the page did.
 */
export const devicesColumnText = {
  // HS-1216: the device's database id. `DeviceViewDTO.Id` is non-nullable on the API and the list
  // already reads it for the row link and `getRowId`; the DTO types it optional, hence the fallback.
  id: (row: DeviceViewDTO) => row.id ?? '',
  // HS-1216: the model alone. This used to append the identifier, as the P2-02 stand-in for the
  // database key; the key now has its own column and the identifier has had one all along, so the
  // filter builder's `model` field matched on two fields and this one matched on neither cleanly.
  model: (row: DeviceViewDTO) => row.model ?? '',
  laboratory: (row: DeviceViewDTO, meta: TableMeta<DeviceViewDTO> | undefined) =>
    firstNonEmpty(row.labId == null ? undefined : meta?.labNames?.get(row.labId), row.labId),
  serialNumber: (row: DeviceViewDTO) => row.serialNumber ?? '',
  identifier: (row: DeviceViewDTO) => row.identifier ?? '',
  // `apiVersion`/`clientAppVersion` match the design's `Sash API`/`Sash client` headers but nothing
  // populates them; `firmwareVersion`/`osVersion` do carry values, so the columns show those instead
  // and say so — see `docs/design-audit/10-list-table-contract.md`.
  firmwareVersion: (row: DeviceViewDTO) => row.firmwareVersion ?? '',
  osVersion: (row: DeviceViewDTO) => row.osVersion ?? '',
} as const;

export type DevicesColumnId = keyof typeof devicesColumnText;

/**
 * The search that links a lab, an institute or a group to its devices (HS-869).
 *
 * The old links carried `?laboratoryId=`/`?instituteId=`, which this list never read — so "View
 * devices" showed every device. `GetApiDeviceParams` accepts all three ids, and the builder
 * already routes them through `filters`, so the link now applies the condition it always claimed
 * to.
 */
export function devicesFilteredBy(
  field: typeof DEVICES_LAB_FIELD | typeof DEVICES_INSTITUTION_FIELD | typeof DEVICES_GROUP_FIELD,
  value: string,
) {
  return {
    ...ADMIN_LIST_SEARCH_DEFAULTS,
    filters: [{ field, operator: 'eq' as const, values: [value] }],
  };
}
